import React from "react";

type BubbleAnim = "none" | "float" | "drift";
type BubbleQuality = "low" | "med" | "high";

interface BubbleFieldProps {
  animation: BubbleAnim;
  quality?: BubbleQuality;
}

const BubbleField: React.FC<BubbleFieldProps> = ({
  animation,
  quality = "med",
}) => {
  const rootRef = React.useRef<HTMLDivElement>(null);

  const base = React.useMemo(
    () => [
      {
        key: "b1",
        size: 300,
        x: "5%",
        y: "2%",
        hue: "gold",
        blur: 140,
        dur: 22,
        dx: 60,
        dy: -180,
        scale: 1.1,
        delay: 0,
      },
      {
        key: "b2",
        size: 460,
        x: "75%",
        y: "85%",
        hue: "dark",
        blur: 180,
        dur: 38,
        dx: -80,
        dy: -220,
        scale: 1.25,
        delay: 4,
      },
      {
        key: "b3",
        size: 260,
        x: "92%",
        y: "40%",
        hue: "gold",
        blur: 120,
        dur: 30,
        dx: -70,
        dy: -160,
        scale: 0.9,
        delay: 2,
      },
      {
        key: "b4",
        size: 240,
        x: "30%",
        y: "65%",
        hue: "dark",
        blur: 110,
        dur: 34,
        dx: 70,
        dy: -190,
        scale: 1.05,
        delay: 6,
      },
      {
        key: "b5",
        size: 130,
        x: "58%",
        y: "18%",
        hue: "gold",
        blur: 80,
        dur: 26,
        dx: 50,
        dy: -130,
        scale: 0.8,
        delay: 3,
      },
      {
        key: "b6",
        size: 180,
        x: "12%",
        y: "78%",
        hue: "gold",
        blur: 90,
        dur: 32,
        dx: 40,
        dy: -150,
        scale: 0.95,
        delay: 1,
      },
    ],
    []
  );

  // --- Disable if reduced motion or none ---
  const disable = React.useMemo(() => {
    if (typeof window === "undefined") return false;
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    return prefersReduced || animation === "none";
  }, [animation]);

  // --- Pause animations when not in viewport ---
  React.useEffect(() => {
    const el = rootRef.current;
    if (!el || disable) return;
    const io = new IntersectionObserver(
      ([e]) => {
        const state = e.isIntersecting ? "running" : "paused";
        el.style.animationPlayState = state as any;
        el.querySelectorAll<HTMLElement>(".bf-anim").forEach(
          (n) => (n.style.animationPlayState = state as any)
        );
      },
      { threshold: 0 } // 👈 more reliable
    );
    io.observe(el);
    return () => io.disconnect();
  }, [disable]);

  if (disable) return null;

  // --- Quality presets ---
  const MOBILE =
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 768px)").matches;
  const presets = {
    low: { count: 3, blurScale: 0.55, sizeScale: 0.7 },
    med: { count: 5, blurScale: 0.8, sizeScale: 0.9 },
    high: { count: 6, blurScale: 1.0, sizeScale: 1.0 },
  } as const;
  const q = presets[quality];
  const count = MOBILE ? Math.min(4, q.count) : q.count;
  const blurScale = MOBILE ? Math.min(0.7, q.blurScale) : q.blurScale;
  const sizeScale = MOBILE ? Math.min(0.8, q.sizeScale) : q.sizeScale;

  const bubbles = base.slice(0, count).map((b) => ({
    ...b,
    size: Math.round(b.size * sizeScale),
    blur: Math.round(b.blur * blurScale),
  }));

  return (
<div
  ref={rootRef}
  className="pointer-events-none absolute inset-0 z-0 overflow-hidden block"
  aria-hidden="true"
>
      {/* 👇 Optional background gradient to make bubbles more visible on black */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#2d2718]/20 to-[#231f1f] z-[-1]" />

      <style>{`
@keyframes bubbleFloat {
  0%   { transform: translate3d(var(--xOff), var(--yOff),0) scale(var(--scale)); opacity:.25; }
  50%  { transform: translate3d(calc(var(--xOff) + var(--dxF)), calc(var(--yOff) + var(--dyF)),0) scale(calc(var(--scale) * 1.05)); opacity:.5; }
  100% { transform: translate3d(var(--xOff), var(--yOff),0) scale(var(--scale)); opacity:.25; }
}
@keyframes bubbleDrift {
  0%   { transform: translate3d(var(--xOff), var(--yOff),0) scale(var(--scale)); opacity:0; }
  10%  { opacity:.35; }
  55%  { transform: translate3d(calc(var(--xOff) + var(--dx)), calc(var(--yOff) + var(--dy)),0) scale(calc(var(--scale) * 1.08)); opacity:.55; }
  90%  { opacity:.35; }
  100% { transform: translate3d(var(--xOff), var(--yOff),0) scale(var(--scale)); opacity:0; }
}
/* Blend-mode fallback for Safari */
@supports not (mix-blend-mode: screen) {
  .bubble { mix-blend-mode: normal; opacity: .35; }
}
@media (prefers-reduced-motion: reduce) { .bf-anim { animation: none !important; } }
      `}</style>

      {bubbles.map((b) => {
        const gradient =
          b.hue === "gold"
            ? "radial-gradient(circle at 30% 30%, rgba(218,180,80,0.55), rgba(218,180,80,0.08) 45%, rgba(218,180,80,0) 70%)"
            : "radial-gradient(circle at 30% 30%, rgba(120,80,30,0.5), rgba(120,80,30,0.07) 40%, rgba(120,80,30,0) 70%)";

        const animClass =
          animation === "float"
            ? `bf-anim animate-[bubbleFloat_${b.dur}s_ease-in-out_${b.delay}s_infinite]`
            : `bf-anim animate-[bubbleDrift_${b.dur}s_linear_${b.delay}s_infinite]`;

        return (
          <div
            key={b.key}
            className={`bubble absolute rounded-full mix-blend-screen ${animClass}`}
            style={{
              left: b.x,
              top: b.y,
              width: b.size,
              height: b.size,
              filter: `blur(${b.blur}px)`,
              background: gradient,
              willChange: "transform, opacity",
              contain: "layout paint style",
              transform: "translateZ(0)",
              ["--xOff" as any]: "0px",
              ["--yOff" as any]: "0px",
              ["--dx" as any]: `${b.dx}px`,
              ["--dy" as any]: `${b.dy}px`,
              ["--dxF" as any]: `${Math.sign(b.dx || 1) * 25}px`,
              ["--dyF" as any]: `${Math.sign(b.dy || -1) * -40}px`,
              ["--scale" as any]: b.scale,
            }}
          />
        );
      })}
    </div>
  );
};

export default BubbleField;
